import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LangProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import NewExpediente from "@/pages/NewExpediente";
import ExpedienteDetail from "@/pages/ExpedienteDetail";
import Profile from "@/pages/Profile";
import Login from "@/pages/Login";

function NetworkErrorScreen({ onRetry }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-red-200 p-6 rounded-sm" data-testid="network-error-screen">
        <div className="coord-label text-red-600">// SERVIDOR NO DISPONIBLE</div>
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mt-2">No se pudo conectar al servidor</h2>
        <p className="text-sm text-slate-600 mt-2">
          El servidor está temporalmente caído (Cloudflare 520). Esto es un problema del entorno de producción, no de tu app.
        </p>
        <ul className="text-sm text-slate-600 mt-3 list-disc pl-5 space-y-1">
          <li>Espera 1–2 minutos y pulsa "Reintentar".</li>
          <li>Si persiste, contacta a <b>Soporte de Emergent</b> mencionando el dominio <code>deslindepro.com</code>.</li>
        </ul>
        <button
          data-testid="retry-network-btn"
          onClick={onRetry}
          className="mt-5 w-full bg-orange-500 hover:bg-orange-400 text-white py-2.5 px-4 rounded-sm font-mono text-sm"
        >
          Reintentar conexión
        </button>
      </div>
    </div>
  );
}

function RequireAuth({ children, allow }) {
  const { role, loading, setupComplete, networkError, refresh } = useAuth();
  const location = useLocation();
  if (networkError) {
    return <NetworkErrorScreen onRetry={() => refresh(true)} />;
  }
  if (loading || setupComplete === null) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono text-sm">Cargando…</div>;
  }
  if (!setupComplete || !role) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (allow && !allow.includes(role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<RequireAuth><Layout /></RequireAuth>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/nuevo" element={<RequireAuth allow={["admin", "operativo"]}><NewExpediente /></RequireAuth>} />
              <Route path="/expediente/:id" element={<ExpedienteDetail />} />
              <Route path="/perfil" element={<RequireAuth allow={["admin"]}><Profile /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
